/**
 * URL Slug Utilities for SEO-friendly URLs
 * 
 * Converts listing data into clean, SEO-optimized URL slugs
 * Example: "Mercury Theatre" -> "mercury-theatre"
 */

/**
 * Convert a string to a URL-friendly slug
 * @param {string} text - Text to convert
 * @returns {string} URL-friendly slug
 */
export const slugify = (text) => {
  if (!text) return '';
  return text
    .toString()
    .toLowerCase()
    .trim()
    .replace(/&/g, 'and')           // Replace & with 'and'
    .replace(/['']/g, '')            // Remove apostrophes
    .replace(/_/g, '-')              // Replace underscores with hyphens
    .replace(/[^\w\s-]/g, '')        // Remove special characters
    .replace(/\s+/g, '-')            // Replace spaces with -
    .replace(/-+/g, '-')             // Replace multiple - with single -
    .replace(/^-+/, '')              // Trim - from start
    .replace(/-+$/, '');             // Trim - from end
};

/**
 * Generate SEO-friendly listing URL (without ID suffix)
 * @param {Object} listing - Listing object with town, category, title
 * @returns {string} SEO-friendly URL path
 */
export const getListingUrl = (listing) => {
  if (!listing) return '/';
  
  const locationSlug = listing.is_outside_essex && listing.region && listing.region !== 'Essex'
    ? slugify(listing.region)
    : slugify(listing.town || 'essex');
  const categorySlug = slugify(listing.category || 'listing');
  const titleSlug = slugify(listing.title || 'untitled');
  
  // Clean URL without ID: /listing/{town-or-region}/{category}/{title}
  return `/listing/${locationSlug}/${categorySlug}/${titleSlug}`;
};

/**
 * Parse listing URL to extract components
 * @param {string} townSlug - Town from URL
 * @param {string} categorySlug - Category from URL  
 * @param {string} titleSlug - Title slug
 * @returns {Object} Parsed components
 */
export const parseListingUrl = (townSlug, categorySlug, titleSlug) => {
  return {
    town: townSlug,
    category: categorySlug,
    titleSlug: titleSlug
  };
};

/**
 * Get old-style listing URL (for backwards compatibility)
 * @param {string} listingId - Listing ID
 * @returns {string} Old URL format
 */
export const getOldListingUrl = (listingId) => {
  return `/listing/${listingId}`;
};

export default { slugify, getListingUrl, parseListingUrl, getOldListingUrl };
